/* ─────────────────────────────────────────────────────────────────
   MERN STACK DEVELOPMENT — MODULE-BASED COURSE CONTENT
   Imported by MicrointernshipTask.jsx
───────────────────────────────────────────────────────────────── */

export const MERN_MODULES = [
  {
    module: 1,
    title: "MongoDB & Express Basics",
    emoji: "🍃",
    topics: [
      {
        id: 1,
        topic: "MongoDB Fundamentals & Schema Design",
        description: "Learn how MongoDB stores data as documents and how to design schemas using Mongoose.",
        motivation: "Great start! Understanding how data is modeled is the foundation every MERN app is built on. 🍃",
        resource: {
          name: "MongoDB Official Docs — Getting Started",
          url: "https://www.mongodb.com/docs/manual/tutorial/getting-started",
        },
        task: "Design and seed a MongoDB database for a Blog application.",
        requirements: [
          "Create a Mongoose schema for Post (title, content, author, tags, createdAt)",
          "Create a Mongoose schema for Comment (postId, name, message, createdAt)",
          "Connect to a free MongoDB Atlas cluster",
          "Write a seed script that inserts at least 5 sample posts",
          "Add basic schema validation (required fields, min/max length)",
          "Include a README.md explaining the schema design",
        ],
      },
      {
        id: 2,
        topic: "Express.js REST API",
        description: "Learn to build a REST API with Express.js that connects to your MongoDB collections.",
        motivation: "Your first real API is live! Express is the backbone of the 'E' in MERN — solid progress. 🛠️",
        resource: {
          name: "Express.js Official Guide — Routing",
          url: "https://expressjs.com/en/guide/routing.html",
        },
        task: "Build a REST API for the Blog application using Express and Mongoose.",
        requirements: [
          "Endpoints: GET /posts, GET /posts/:id, POST /posts, PUT /posts/:id, DELETE /posts/:id",
          "Endpoint to add a comment to a post: POST /posts/:id/comments",
          "Proper HTTP status codes (200, 201, 404, 400)",
          "Input validation for POST/PUT requests",
          "Use async/await with proper error handling (try/catch)",
          "Test all endpoints using Postman or Thunder Client",
        ],
      },
    ],
  },
  {
    module: 2,
    title: "React Frontend",
    emoji: "⚛️",
    topics: [
      {
        id: 3,
        topic: "React Components & State",
        description: "Learn to build reusable React components and manage state with hooks.",
        motivation: "Your UI has structure and memory now! Components and state are the heart of every React app. ⚛️",
        resource: {
          name: "React Official Docs — State: A Component's Memory",
          url: "https://react.dev/learn/state-a-components-memory",
        },
        task: "Build the frontend UI for the Blog (static data first, no API yet).",
        requirements: [
          "PostList component displaying post cards (title, author, excerpt)",
          "PostCard component reused for each post",
          "A 'like' button on each post that updates a like count using state",
          "A search/filter input that filters posts by title in real time",
          "Responsive layout (mobile and desktop)",
          "Clean component folder structure",
        ],
      },
      {
        id: 4,
        topic: "React Router & Consuming APIs",
        description: "Learn client-side routing and how to fetch and display data from a real API using Axios or Fetch.",
        motivation: "Multi-page navigation and live data — your app finally feels like a real product! 🌐",
        resource: {
          name: "React Router Official Docs — Tutorial",
          url: "https://reactrouter.com/en/main/start/tutorial",
        },
        task: "Connect your Blog frontend to your Express API and add routing.",
        requirements: [
          "Route '/' — list all posts fetched from GET /posts",
          "Route '/posts/:id' — full post detail fetched from GET /posts/:id",
          "Route '/create' — form to create a new post (POST /posts)",
          "Loading and error states while fetching data",
          "Navigation bar with links between routes",
          "Use Axios or Fetch consistently across the app",
        ],
      },
    ],
  },
  {
    module: 3,
    title: "Full Integration & Authentication",
    emoji: "🔐",
    topics: [
      {
        id: 5,
        topic: "Connecting MERN — Full CRUD Application",
        description: "Bring the full stack together — complete Create, Read, Update, Delete flows across frontend and backend.",
        motivation: "Full CRUD, full stack, fully connected. This is the complete MERN loop working end-to-end! 🔄",
        resource: {
          name: "MongoDB — MERN Stack Tutorial",
          url: "https://www.mongodb.com/languages/mern-stack-tutorial",
        },
        task: "Complete full CRUD functionality across your Blog app's frontend and backend.",
        requirements: [
          "Edit an existing post from the UI (PUT request)",
          "Delete a post from the UI with a confirmation prompt (DELETE request)",
          "Add comments to a post from the UI and display them",
          "Optimistic UI update or refetch after each mutation",
          "Handle and display API errors clearly to the user",
          "All changes persist correctly in MongoDB",
        ],
      },
      {
        id: 6,
        topic: "JWT Authentication (MERN)",
        description: "Add user registration, login, and protected routes to your MERN application using JWT.",
        motivation: "Security unlocked! You now understand how real MERN apps protect users and their data. 🔐",
        resource: {
          name: "JWT Introduction — jwt.io",
          url: "https://jwt.io/introduction",
        },
        task: "Add authentication to your Blog app so only logged-in users can create posts.",
        requirements: [
          "POST /auth/register — hash passwords with bcrypt",
          "POST /auth/login — return a JWT token",
          "Protect POST/PUT/DELETE post routes with auth middleware",
          "Store the JWT on the frontend and attach it to authenticated requests",
          "Show login/register forms and a logged-in state in the UI",
          "Redirect unauthenticated users away from the create-post page",
        ],
      },
    ],
  },
  {
    module: 4,
    title: "Final Project",
    emoji: "🏆",
    topics: [
      {
        id: 7,
        topic: "Full Stack MERN Blog Platform",
        description: "Apply everything learned throughout the MicroIntern to ship a complete, authenticated MERN application.",
        motivation: "Incredible! You built a secure, full-featured MERN application from scratch. That's certificate-worthy. 🎉",
        resource: {
          name: "Fullstack Open — University of Helsinki",
          url: "https://fullstackopen.com/en/",
        },
        task: "Polish and complete your Blog Platform into a fully working MERN application.",
        requirements: [
          "User registration, login, and JWT-protected post creation",
          "Full CRUD for posts, with comments per post",
          "Author name shown on each post, linked to the logged-in user",
          "Search and filter posts by title or tag",
          "Responsive UI across mobile, tablet, and desktop",
          "Loading and error states handled throughout",
          "Clean folder structure for both client and server",
          "Well-documented README.md with setup instructions",
        ],
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────
   Flat array — used by COURSE_TASKS in MicrointernshipTask.jsx
   Preserves backward-compatible indexing for completedTopics[]
───────────────────────────────────────────────────────────────── */
export const MERN_TASKS = MERN_MODULES.flatMap((m) => m.topics);
